import { useState, useEffect, useMemo, useRef } from "react";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  getCheckboxMinMax,
  getCheckboxSelectedCount,
  getAutoCheckboxInstructionText,
  getCheckboxMinNotMetMessage,
} from "../../lib/checkboxConstraints";

const Checkboxes = ({
  itemkey,
  content,
  onChange,
  defaultValue,
  value: valueProp = undefined,
  validationAttempt = 0,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const isControlled = valueProp !== undefined && valueProp !== null;
  const [internalValue, setInternalValue] = useState(defaultValue ?? {});
  const [submitErrorVisible, setSubmitErrorVisible] = useState(false);
  const prevAttemptRef = useRef(0);

  const value = isControlled
    ? (typeof valueProp === "object" && valueProp !== null && !Array.isArray(valueProp)
        ? valueProp
        : {})
    : internalValue;

  const { minSelections, maxSelections } = useMemo(
    () => getCheckboxMinMax(content),
    [content],
  );

  const selectedCount = useMemo(() => getCheckboxSelectedCount(value), [value]);

  const showInstructionToUser = content.showSelectionInstruction !== false;
  const autoInstruction = useMemo(
    () => getAutoCheckboxInstructionText(content, t),
    [content, t],
  );
  const minNotMetMessage = useMemo(
    () => getCheckboxMinNotMetMessage(content, t),
    [content, t],
  );

  useEffect(() => {
    if (validationAttempt === 0) {
      prevAttemptRef.current = 0;
      setSubmitErrorVisible(false);
    }
  }, [validationAttempt]);

  useEffect(() => {
    if (readOnly) return;
    if (validationAttempt > prevAttemptRef.current) {
      prevAttemptRef.current = validationAttempt;
      if (selectedCount < minSelections) {
        setSubmitErrorVisible(true);
      }
    }
  }, [validationAttempt, readOnly, selectedCount, minSelections]);

  useEffect(() => {
    if (selectedCount >= minSelections) {
      setSubmitErrorVisible(false);
    }
  }, [selectedCount, minSelections]);

  const handleOptionValueChange = (optkey, val) => {
    if (onChange) {
      if (val === true && selectedCount >= maxSelections) {
        return;
      }

      const newValue = { ...value, [optkey]: val };
      if (!isControlled) setInternalValue(newValue);
      onChange(content.key, newValue);
    } else if (!isControlled) {
      setInternalValue(defaultValue ?? {});
    }
  };

  useEffect(() => {
    if (isControlled) return;
    let val = defaultValue ?? {};
    if (typeof val === "string" || Array.isArray(val)) val = {};
    setInternalValue(val);
  }, [isControlled, defaultValue]);

  const invalid = selectedCount < minSelections;
  const showProblem = !readOnly && submitErrorVisible && invalid;

  let bannerText = null;
  let bannerError = false;
  if (showInstructionToUser && autoInstruction) {
    bannerText = autoInstruction;
    bannerError = showProblem;
  } else if (showProblem && minNotMetMessage) {
    bannerText = minNotMetMessage;
    bannerError = true;
  }

  const hasConstraints = minSelections > 0 || maxSelections < Infinity;
  const constraintHint = hasConstraints
    ? minSelections === maxSelections
      ? t("select_exactly_n", { count: minSelections }) || `Select exactly ${minSelections}`
      : minSelections > 0 && maxSelections < Infinity
        ? t("select_between_n_and_m", { min: minSelections, max: maxSelections }) || `Select ${minSelections}-${maxSelections}`
        : minSelections > 0
          ? t("select_at_least_n", { count: minSelections }) || `Select at least ${minSelections}`
          : t("select_up_to_n", { count: maxSelections }) || `Select up to ${maxSelections}`
    : null;

  const handleDisabledClick = () => {
    toast.info(t("maximum_reached") || `Maximum ${maxSelections} selections reached`);
  };

  return (
    <div className="text-start mt-3">
      <div className="mb-2">
        <Label className="font-semibold text-gray-800 text-sm">
          {content.label}
        </Label>
        {constraintHint && (
          <span className={
            bannerError
                ? "text-xs gap-1.5 font-medium bg-red-600/10 px-2.5 py-0.5 inline-flex justify-center items-center rounded-full text-red-600  border box-border border-red-200"
                : "text-xs text-primary font-medium bg-primary/10 px-2.5 py-0.5 rounded-full"
          }> {constraintHint}
          </span>
        )}
      </div>

      {/* Progress bar when there are constraints */}
      {hasConstraints && (
        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${Math.min((selectedCount / maxSelections) * 100, 100)}%` }}
            />
          </div>
          <span className="text-xs text-gray-500 tabular-nums shrink-0">
            {selectedCount}/{maxSelections}
          </span>
        </div>
      )}
{/*

      {bannerText && (
        <div
          className={
            bannerError
              ? "flex items-center gap-1.5 text-xs text-red-600 mb-2 bg-gradient-to-r from-red-50 to-orange-50 p-2 rounded-lg border border-red-200"
              : "text-xs text-blue-700 mb-2 bg-blue-50 p-2 rounded-lg border border-blue-200 font-medium"
          }
        >
          {bannerError && (
            <AlertCircle className="w-3 h-3 flex-shrink-0" aria-hidden />
          )}
          <span>{bannerText}</span>
        </div>
      )}
*/}

      <div className="space-y-1.5">
        {content.options.map((option) => {
          const isChecked = value?.[option.key] === true;
          const isDisabled = readOnly || (!isChecked && selectedCount >= maxSelections);

          return (
            <div
              key={option.key}
              onClick={isDisabled ? handleDisabledClick : undefined}
              className={`flex items-center min-h-[44px] px-3 py-2 rounded-lg border transition-all duration-150 ${
                isChecked
                  ? "bg-primary/5 border-primary/40 border-s-2 border-s-primary"
                  : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              } ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <Checkbox
                id={`${itemkey}_${option.key}`}
                checked={isChecked}
                disabled={isDisabled}
                onCheckedChange={(value) =>
                  handleOptionValueChange(option.key, value)
                }
              />
              <Label
                htmlFor={`${itemkey}_${option.key}`}
                className={`text-sm mx-2 flex-1 ${
                  isDisabled ? "cursor-not-allowed" : "cursor-pointer"
                } ${isChecked ? "font-medium text-primary" : "font-normal text-gray-700"}`}
              >
                {option.label}
              </Label>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Checkboxes;
